import { z } from "zod";
import { blockSchema } from "../blocks.js";

/**
 * A file on a message, as either client sees it. Carries an id and the
 * shape of the image, never a URL: the bytes live in a private bucket, and
 * each client builds a fetch URL it can authenticate with — the widget with
 * its session token, the panel with its cookie. A signed URL baked in here
 * would either expire inside an open page or outlive the conversation.
 *
 * `width`/`height` are here so a transcript can reserve the right space
 * before the image loads, rather than reflowing as each one arrives.
 */
export const attachmentDtoSchema = z.object({
  id: z.string().uuid(),
  kind: z.literal("image"),
  mimeType: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  bytes: z.number().int(),
});
export type AttachmentDto = z.infer<typeof attachmentDtoSchema>;

/**
 * A message as the *widget* sees it. Deliberately excludes `model`,
 * `tokensIn`/`tokensOut`, `costUsd`, `toolCalls` and `authorUserId` — those
 * are agency-internal (see PLAN.md test 28, "No domain leakage on the
 * wire").
 */
export const messageDtoSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int().nonnegative(),
  role: z.enum(["user", "assistant", "agent", "system"]),
  content: z.string().nullable(),
  blocks: z.array(blockSchema).nullable(),
  attachments: z.array(attachmentDtoSchema).default([]),
  createdAt: z.string().datetime(),
});
export type MessageDto = z.infer<typeof messageDtoSchema>;
